import { posToDOMRect } from '@tiptap/core';
import { VideoCameraSmall, YoutubeSmall } from '@wix/wix-ui-icons-common';

import { TIPTAP_VIDEO_TYPE } from './../ricos-content';
import type { VideoData } from './../ricos-schema/legacy/wix/rich_content/v1';
import { type EditorPluginCreator, quickActionsModalSections } from './../ricos-types';
import { RicosPluginToolbarNames } from './../ricos-types';
import { changeSelectionToEndOfTextBlock } from './../wix-rich-content-common';

import { pluginFigure } from '../plugin-figure/editor';
import {
  RICOS_DEFAULTS,
  VIDEO_INSERT_VIDEO_MODAL_ID,
  VIDEO_INSERT_YOUTUBE_MODAL_ID,
} from './constants';
import { videoConverter } from './converter/video-converter';
import { DEFAULT_CONFIGS } from './defaults';
import { getAddButtons } from './getAddButtons';
import { getToolbarButtons } from './getToolbarButtons';
import { InsertModal } from './modals/InsertModal';
import { SettingsModal } from './modals/SettingsModal';
import { tiptapExtensions } from './tiptap/tiptap';
import type { VideoPluginEditorConfig } from './types';
import { VIDEO_TYPE, videoButtonsTypes } from './types';

export const pluginVideo: EditorPluginCreator<
  VideoPluginEditorConfig,
  { SettingsModal: typeof SettingsModal; InsertModal: typeof InsertModal }
> = config => {
  const pluginConfig: VideoPluginEditorConfig = { ...DEFAULT_CONFIGS.editor, ...config };
  return {
    config: pluginConfig,
    type: VIDEO_TYPE,
    pmMarkConverters: [],
    pmNodeConverters: [videoConverter],
    tiptapExtensions,
    getAddButtons,
    toolbar: {
      names: [RicosPluginToolbarNames.VIDEO],
      getButtons: (config, services) => getToolbarButtons(config, services),
      resolveReferenceElement: tiptapEditor => {
        const startNodePos = tiptapEditor.state.selection.$from;
        if (startNodePos.nodeAfter?.type.name !== TIPTAP_VIDEO_TYPE) {
          return null;
        }
        return tiptapEditor.view.nodeDOM(startNodePos.pos - 1) as HTMLElement | null;
      },
    },
    getQuickActions: ({ config, pluginServices }) => {
      const {
        disableDownloadDefault,
        enableCustomUploadOnMobile,
        getVideoUrl,
        handleFileSelection,
        handleFileUpload,
        onVideoSelected,
        disableDownload,
      } = config || {};

      const modalBaseProps = {
        disableDownloadDefault,
        enableCustomUploadOnMobile,
        getVideoUrl,
        handleFileSelection,
        handleFileUpload,
        onVideoSelected,
      };

      const isMobile = pluginServices?.context.isMobile;

      return [
        {
          id: 'video',
          title: 'QuickActionsPlugin_ActionTitle_Video',
          icon: VideoCameraSmall,
          section: quickActionsModalSections.Media,
          tags: ['clip', 'movie', 'film', 'record', 'stream'],
          command: ({ editor, modalService }) => {
            changeSelectionToEndOfTextBlock(editor);

            modalService.togglePluginPanel({
              id: VIDEO_INSERT_VIDEO_MODAL_ID,
              Component: InsertModal,
              componentProps: {
                componentData: { ...RICOS_DEFAULTS, disableDownload } as VideoData,
                modalId: VIDEO_INSERT_VIDEO_MODAL_ID,
                ...modalBaseProps,
              },
              isMobile,
              positioning: {
                referenceElement: {
                  getBoundingClientRect: () =>
                    posToDOMRect(
                      editor.view,
                      editor.view.state.selection.from,
                      editor.view.state.selection.to
                    ),
                },
                placement: 'bottom-end',
              },
              mobileLayout: 'drawer',
            });
          },
        },
        {
          id: 'youtubeVideo',
          title: 'QuickActionsPlugin_ActionTitle_YoutubeVideo',
          icon: YoutubeSmall,
          section: quickActionsModalSections.Embed,
          tags: ['clip', 'movie', 'film', 'record', 'stream', 'link', 'content'],
          command: ({ editor, modalService }) => {
            changeSelectionToEndOfTextBlock(editor);

            modalService.togglePluginPanel({
              id: VIDEO_INSERT_YOUTUBE_MODAL_ID,
              Component: InsertModal,
              componentProps: {
                componentData: { ...RICOS_DEFAULTS, type: videoButtonsTypes.youTube } as VideoData,
                modalId: VIDEO_INSERT_YOUTUBE_MODAL_ID,
                ...modalBaseProps,
              },
              isMobile,
              positioning: {
                referenceElement: {
                  getBoundingClientRect: () =>
                    posToDOMRect(
                      editor.view,
                      editor.view.state.selection.from,
                      editor.view.state.selection.to
                    ),
                },
                placement: 'bottom-end',
              },
              mobileLayout: 'drawer',
            });
          },
        },
      ];
    },
    dependencies: [pluginFigure],
    __exports: { SettingsModal, InsertModal },
  };
};
